import {
  Button as AntButton,
  Divider,
  Modal,
  Pagination,
  Select,
  Spin,
  Switch,
  Tooltip,
} from "antd";
import {
  MODULE_KEY,
  fetchOfficeCollectionMapping,
  importFile,
} from "./actions";
import TableFilter from "src/components/common/TableFilter/TableFilter";
import {
  CloseOutlined,
  DoubleLeftOutlined,
  DoubleRightOutlined,
  DownloadOutlined,
  EditOutlined,
  PlusOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import Breadcrumb from "src/components/common/Breadcrumb/Breadcrumb";
import Button from "src/components/common/Button/Button";
import { useEffect, useMemo, useRef, useState } from "react";
import filterColumns from "./filterColumns";
import "./../../../styles/vendor.scss";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteOPlanogram,
  getFADetail,
  getFrameCollectionForOffice,
  getOfficeCollectionFilterData,
} from "./sagas";
import AddOCMapping from "./AddOCMapping";
import { UploadModal } from "src/components/common/Uploads/UploadModal";
import { ACTION } from "src/util/utils";
import { ValidationModal } from "./ValidationModal";
import {
  showErrorNotification,
  showSuccessNotification,
} from "src/util/notifications";
import dayjs from "dayjs";
import { OFFICE_COLLECTION_EXCEL_COLUMNS } from "src/util/columns";
const ExcelJS = require("exceljs");
import { ImportType } from "src/util/utils";
import { useMsal } from "@azure/msal-react";
import CloseOffice from "./closeOffice";
import useRolePermission from "src/hooks/useRolePermission";
import Label from "src/components/common/Label/Label";
import GenericGrid from "src/components/common/Grid/GenericGrid";
import {
  MRT_ColumnFiltersState,
  MRT_RowSelectionState,
  MRT_SortingState,
} from "material-react-table";
import { OfficeCollectionFilterOptions } from "./types";
import {
  areColumnFiltersEqual,
  normalizeColumnFilters,
  filterRowsByColumnFilters,
} from "src/util/grid/gridFilter.utils";

const buildOfficeCollectionFilterOptionsFromRows = (
  rows: any[]
): OfficeCollectionFilterOptions => {
  const vendorMap = new Map<string, { text: string; value: string }>();
  const collectionMap = new Map<string, { text: string; value: string }>();
  const collectionTypeMap = new Map<string, { text: string; value: string }>();

  (rows || []).forEach((row: any) => {
    const vendor = row?.Vendor?.trim?.() ?? "";
    const collection = row?.Collection?.trim?.() ?? "";
    const collectionType = row?.CollectionType?.trim?.() ?? "";

    if (vendor) vendorMap.set(vendor, { text: vendor, value: vendor });
    if (collection)
      collectionMap.set(collection, { text: collection, value: collection });
    if (collectionType)
      collectionTypeMap.set(collectionType, {
        text: collectionType,
        value: collectionType,
      });
  });

  const sortByText = (
    left: { text: string; value: string },
    right: { text: string; value: string }
  ) => left.text.toLowerCase().localeCompare(right.text.toLowerCase());

  return {
    vendorName: Array.from(vendorMap.values()).sort(sortByText),
    collectionName: Array.from(collectionMap.values()).sort(sortByText),
    collectionTypeName: Array.from(collectionTypeMap.values()).sort(sortByText),
  };
};

const toDateTime = (value: any) => {
  if (!value || value === "null") return null;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
};

const toSortableNumber = (value: any) => {
  if (value === null || value === undefined || value === "") return null;

  // Handle formatted numeric strings like "1,234,567" and " 12345 ".
  const normalizedValue =
    typeof value === "string" ? value.replace(/,/g, "").trim() : value;
  const num = Number(normalizedValue);
  return Number.isFinite(num) ? num : null;
};

const getSortValue = (row: any, sortId: string) => {
  const rawValue = row?.[sortId];

  switch (sortId) {
    case "ArrangerQty":
    case "FrameArrangerID":
      return toSortableNumber(rawValue);
    case "UpdatedDate":
    case "StopDate":
      return toDateTime(rawValue);
    default:
      // For other fields, prefer numeric compare when the value is numeric-like,
      // else use string compare.
      return toSortableNumber(rawValue) ?? (rawValue ?? "");
  }
};

const compareSortValues = (left: any, right: any) => {
  if (left === right) return 0;
  if (left === null || left === undefined || left === "") return 1;
  if (right === null || right === undefined || right === "") return -1;

  const leftType = typeof left;
  const rightType = typeof right;

  if (leftType === "number" && rightType === "number") {
    return left - right;
  }

  return String(left).localeCompare(String(right), "en", {
    numeric: true,
    sensitivity: "base",
  });
};

function OfficeCollectionMapping() {
  const dispatch = useDispatch();
  const { accounts }: any = useMsal();
  const FREE_TEXT_SEARCH_DEBOUNCE_MS = 500;
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [modalData, setModalData] = useState([]);
  const [visible, setVisible] = useState(false);
  const [closeOfficeVisible, setCloseOfficeVisible] = useState(false);
  const [openUpload, setOpenUpload] = useState<boolean>(false);
  const [open, setOpen] = useState(false);
  const [frameCollection, setFrameCollection] = useState();
  const [visibleDelete, setVisibleDelete] = useState(false);
  const [deleteData, setDeleteData] = useState(false);
  const { hasPermission } = useRolePermission();
  const [selectedRow, setSelectedRow] = useState<any>([]);
  const [bulkEditModal, setBulkEditModal] = useState(false);
  const [sameType, setSameType] = useState<any>([]);
  const [showActive, setShowActive] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [columnFilters, setColumnFilters] =
    useState<MRT_ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [filterOptions, setFilterOptions] =
    useState<OfficeCollectionFilterOptions>({
      vendorName: [],
      collectionName: [],
      collectionTypeName: [],
    });
  const hasMounted = useRef(false);
  const freeTextSearchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const stateKey = `${MODULE_KEY}${ACTION.STATE_KEY}`;

  const officeCollectionState = useSelector((state: any) => state[stateKey]);
  const importData = officeCollectionState.importValidationData;
  const totalCount = officeCollectionState.totalCount || 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const [errorModal, setErrorModal] = useState(false);

  const clearPendingFreeTextSearch = () => {
    if (freeTextSearchTimeoutRef.current) {
      clearTimeout(freeTextSearchTimeoutRef.current);
      freeTextSearchTimeoutRef.current = null;
    }
  };

  const areFiltersEqual = (
    left: MRT_ColumnFiltersState,
    right: MRT_ColumnFiltersState
  ) => areColumnFiltersEqual(left, right);

  const fetchOfficeCollectionPage = (
    isActive: boolean,
    page: number,
    size: number,
    filtersArg: MRT_ColumnFiltersState = columnFilters,
    sortingArg: MRT_SortingState = sorting
  ) => {
    clearPendingFreeTextSearch();
    dispatch(
      fetchOfficeCollectionMapping({
        isActive,
        pageNumber: page,
        pageSize: size,
        filters: filtersArg,
        sorting: sortingArg,
      })
    );
  };

  useEffect(() => {
    return () => clearPendingFreeTextSearch();
  }, []);

  useEffect(() => {
    let isSubscribed = true;

    const loadFilterOptions = async () => {
      try {
        const allRows = await getOfficeCollectionFilterData(showActive);
        if (!isSubscribed) return;
        setFilterOptions(buildOfficeCollectionFilterOptionsFromRows(allRows));
      } catch {
        if (!isSubscribed) return;
        setFilterOptions({
          vendorName: [],
          collectionName: [],
          collectionTypeName: [],
        });
      }
    };

    loadFilterOptions();

    return () => {
      isSubscribed = false;
    };
  }, [showActive]);

  const closeModal = (e: React.MouseEvent<HTMLButtonElement>) => {
    setOpen(true);
  };
  useEffect(() => {
    if (hasMounted.current) return;
    hasMounted.current = true;
    fetchOfficeCollectionPage(showActive, pageNumber, pageSize);
  }, []);
  const addDrawer = (e: React.MouseEvent<HTMLButtonElement>) => {
    setVisible(true);
  };
  const bulkEditModalOpen = () => {
    setVisible(false);
    setBulkEditModal(true);
  };
  const editDrawer = (record: any) => {
    let payload: any = 0;
    let skuData: any = [];
    if (selectedRow?.length > 1) {
      setVisible(false);
      selectedRow?.map((item: any) => {
        selectedRow?.map((ele: any, index: any) => {
          if (
            item.AssortmentName !== ele.AssortmentName ||
            item.CollectionType !== ele.CollectionType
          ) {
            skuData.push({
              Assortment: ele.AssortmentName,
              CollectionType: ele?.CollectionType,
            });
            bulkEditModalOpen();
          }
        });
      });
      const uniqueAssortments = Array.from(
        new Set(skuData.map((item: any) => item.Assortment))
      );
      const uniqueCollectionTypes = Array.from(
        new Set(skuData.map((item: any) => item.CollectionType))
      );
      setSameType({
        Assortment: uniqueAssortments.join(", "),
        CollectionType: uniqueCollectionTypes.join(", "),
      });
      if (skuData.length <= 0) {
        const res: any = {
          FrameArrangerID: selectedRow[0]?.FrameArrangerID,
          CollectionType: selectedRow[0]?.CollectionType,
        };
        setVisible(true);
        setModalData(res);
      }
      const ids = selectedRow.map((row: any) => row.ID);
    } else {
      payload = [record?.ID];
      setVisible(true);
      const res = getFADetail(payload);
      res.then((response: any) => {
        setModalData(response);
      });
    }
    const frames = getFrameCollectionForOffice(payload);
    frames.then((response: any) => {
      setFrameCollection(response);
    });
  };
  const deleteModal = (record: any) => {
    setVisibleDelete(true);
    setDeleteData(record);
  };

  const handleCancel = () => {
    setVisible(false);
    setSelectedRow([]);
    setRowSelection({});
    setCloseOfficeVisible(false);
    fetchOfficeCollectionPage(showActive, pageNumber, pageSize);
  };

  let filterValues = filterColumns(editDrawer, deleteModal, filterOptions);
  const sortedGridData = useMemo(() => {
    const rows = Array.isArray(filterValues?.data) ? [...filterValues.data] : [];
    const primarySort = sorting?.[0];

    if (!primarySort?.id) {
      return rows;
    }

    rows.sort((left: any, right: any) => {
      const leftValue = getSortValue(left, primarySort.id);
      const rightValue = getSortValue(right, primarySort.id);
      const comparison = compareSortValues(leftValue, rightValue);

      if (comparison !== 0) {
        return primarySort.desc ? comparison * -1 : comparison;
      }

      // Stable tie-breaker so ordering is deterministic for equal values.
      return String(left?.OfficeNum ?? "").localeCompare(
        String(right?.OfficeNum ?? ""),
        "en",
        { numeric: true, sensitivity: "base" }
      );
    });

    return rows;
  }, [filterValues?.data, sorting]);
  const displayedGridData = useMemo(() => {
    return filterRowsByColumnFilters(sortedGridData, columnFilters);
  }, [sortedGridData, columnFilters]);
  useEffect(() => {
    try {
      if (typeof importData === "string") {
        showSuccessNotification(importData);
        fetchOfficeCollectionPage(showActive, pageNumber, pageSize);
      } else if (Object.keys(importData)?.length) {
        setErrorModal(true);
        fetchOfficeCollectionPage(showActive, pageNumber, pageSize);
      }
    } catch (error: any) {
      if (error && error?.response) {
        if (error?.response?.data) {
          showErrorNotification(error.response.data.error.messages[0]);
        } else {
          showErrorNotification(error.response);
        }
      }
    }
  }, [importData]);
  const exportExcelFile = async () => {
    setConfirmLoading(true);
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("My Sheet");
    //sheet.properties.defaultRowHeight = 25;

    sheet.columns = OFFICE_COLLECTION_EXCEL_COLUMNS;
    const allRows = await getOfficeCollectionFilterData(showActive);
    const allGridRows = Array.isArray(allRows) ? [...allRows] : [];
    const primarySort = sorting?.[0];

    if (primarySort?.id) {
      allGridRows.sort((left: any, right: any) => {
        const leftValue = getSortValue(left, primarySort.id);
        const rightValue = getSortValue(right, primarySort.id);
        const comparison = compareSortValues(leftValue, rightValue);

        if (comparison !== 0) {
          return primarySort.desc ? comparison * -1 : comparison;
        }

        return String(left?.OfficeNum ?? "").localeCompare(
          String(right?.OfficeNum ?? ""),
          "en",
          { numeric: true, sensitivity: "base" }
        );
      });
    }

    const data = filterRowsByColumnFilters(allGridRows, columnFilters);
    try {
      if (data?.length > 0) {
        setOpen(false);

        data?.forEach((item: any) => {
          sheet.addRow({
            OfficeNum: item.OfficeNum,
            LocationName: item.LocationName,
            Vendor: item.Vendor,
            Collection: item.Collection,
            CollectionType: item.CollectionType,
            FrameArrangerID: item.FrameArrangerID,
            ArrangerQty: item.ArrangerQty,
            UpdatedDate:
              item.UpdatedDate !== null
                ? dayjs(item.UpdatedDate).format("YYYY-MM-DD")
                : "",
            UpdateNotes: item.UpdateNotes,
            AssortmentName: item.AssortmentName,
            StopDate:
              item.StopDate !== null
                ? dayjs(item.StopDate).format("YYYY-MM-DD")
                : "",
          });
        });

        const res = await workbook.xlsx.writeBuffer();
        const blob = new Blob([res], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = "Office-Collection-Report.csv";
        anchor.click();
        window.URL.revokeObjectURL(url);

        setConfirmLoading(false);
        showSuccessNotification("Excel downloaded successfully");
      } else {
        setOpen(false);
        setConfirmLoading(false);
        showErrorNotification("No data available for the selected date");
      }
    } catch (error: any) {
      setOpen(false);
      setConfirmLoading(false);
      showErrorNotification(error.response.data.error.messages[0]);
    }
  };

  const handleDelete = () => {
    setConfirmLoading(true);
    const payload = {
      ID: deleteData,
      CreatedBy: accounts?.[0]?.username,
    };
    deleteOPlanogram(payload)
      .then((res: any) => {
        showSuccessNotification(res);
        handleCancel();
        setConfirmLoading(false);
      })
      .catch((error: any) => {
        showErrorNotification(error.response.data.error.messages[0]);
        setConfirmLoading(false);
      });

    setVisibleDelete(false);
  };

  useEffect(() => {
    const selectedIds = Object.keys(rowSelection).filter(id => rowSelection[id]);
    const selectedRows = selectedIds.map(index => displayedGridData[parseInt(index)]);
    setSelectedRow(selectedRows);
  }, [rowSelection, displayedGridData]);
  return (
    <>
      <Spin spinning={confirmLoading}>
        <Divider />
        <Breadcrumb>
          <span>Office Planograms</span>
        </Breadcrumb>
        <TableFilter>
          <div className="form align-items-end flex"></div>
          <div className="d-flex justify-content-end gap-2">
            <Label
              label="Show Active"
              className="flex-row align-items-center fs-6 gap-1"
            >
              <Switch
                size="small"
                checked={showActive}
                onChange={(flag) => {
                  setShowActive(flag);
                  setPageNumber(1);
                  setSelectedRow([]);
                  setRowSelection({});
                  fetchOfficeCollectionPage(flag, 1, pageSize, columnFilters, sorting);
                }}
              />
            </Label>
            <Button
              size="middle"
              icon={<DownloadOutlined />}
              ant-click-animating-without-extra-node="true"
              onClick={closeModal}
              disabled={totalCount <= 0}
            >
              {" "}
              Export
            </Button>
            {hasPermission("officecollectionsmapping_import") && (
              <Button
                size="middle"
                icon={<UploadOutlined />}
                ant-click-animating-without-extra-node="true"
                onClick={() => setOpenUpload(true)}
              >
                {" "}
                Import
              </Button>
            )}
            <div>
              {hasPermission("officecollectionsmapping_add") && (
                <Button
                  size="middle"
                  icon={<PlusOutlined />}
                  style={{ marginRight: "10px" }}
                  ant-click-animating-without-extra-node="true"
                  onClick={addDrawer}
                >
                  {" "}
                  Add New
                </Button>
              )}
            </div>
            {hasPermission("officecollectionsmapping_bulkedit") && (
              <Button
                size="middle"
                icon={<EditOutlined />}
                ant-click-animating-without-extra-node="true"
                disabled={selectedRow?.length < 2}
                onClick={editDrawer}
              >
                {" "}
                Bulk Edit
              </Button>
            )}
            {hasPermission("officecollectionsmapping_close") && (
              <Button
                size="middle"
                icon={<CloseOutlined />}
                ant-click-animating-without-extra-node="true"
                onClick={() => setCloseOfficeVisible(true)}
              >
                {" "}
                Close Office
              </Button>
            )}
          </div>
        </TableFilter>
        <GenericGrid
          columns={filterValues.columns}
          data={displayedGridData}
          enableRowSelection={true}
          onRowSelectionChange={setRowSelection}
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
          filterDisplayMode="popover"
          highlightFilterMatch={true}
          enablePagination={false}
          enableBottomToolbar={false}
          enableStickyHeader={true}
          enableStickyFooter={true}
          enableColumnPinning={true}
          enableColumnOrdering={false}
          manualFiltering={true}
          manualSorting={true}
          columnFilters={columnFilters}
          sorting={sorting}
          onColumnFiltersChange={(nextFilters: MRT_ColumnFiltersState) => {
            const normalizedNextFilters = normalizeColumnFilters(nextFilters);

            if (areFiltersEqual(columnFilters, normalizedNextFilters)) {
              return;
            }

            setColumnFilters(normalizedNextFilters);
            setPageNumber(1);
            setSelectedRow([]);
            setRowSelection({});

            const hasFreeTextFilter = normalizedNextFilters.some(
              (filter) => typeof filter?.value === "string"
            );

            if (hasFreeTextFilter) {
              clearPendingFreeTextSearch();
              freeTextSearchTimeoutRef.current = setTimeout(() => {
                fetchOfficeCollectionPage(showActive, 1, pageSize, normalizedNextFilters, sorting);
                freeTextSearchTimeoutRef.current = null;
              }, FREE_TEXT_SEARCH_DEBOUNCE_MS);
              return;
            }

            fetchOfficeCollectionPage(showActive, 1, pageSize, normalizedNextFilters, sorting);
          }}
          onSortingChange={(nextSorting: MRT_SortingState) => {
            setSorting(nextSorting);
            setPageNumber(1);
            setSelectedRow([]);
            setRowSelection({});
            fetchOfficeCollectionPage(showActive, 1, pageSize, columnFilters, nextSorting);
          }}
          columnPinning={{}}
          isLoading={filterValues.loading}
          muiTableBodyCellProps={{
            sx: {
              fontSize: '11px',
              textAlign: 'center',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
            },
          }}
          muiTableHeadCellProps={{
            sx: {
              fontSize: '10px',
              fontWeight: '800',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
              textAlign: 'center',
              padding: "5px 10px",
            },
          }}
          muiTableBodyRowProps={{
            sx: {
              "& td": {
                padding: "5px 10px",
              }
            },
          }}>
        </GenericGrid>
        <div className="d-flex justify-content-between align-items-center mt-2">
          <div
            className="total-div"
            style={{ margin: 0, marginLeft: 0, marginRight: 0, marginTop: 0, right: 0 }}
          >
            TOTAL RECORDS <span className="total-count">{totalCount}</span>
          </div>
          <div className="d-flex align-items-center gap-2 pagination-ui-match">
            <span style={{ fontSize: "12px" }}>Rows per page:</span>
            <Select
              value={pageSize}
              style={{ width: 90 }}
              options={[10, 50, 100, 500, 1000].map((size) => ({
                label: size.toString(),
                value: size,
              }))}
              onChange={(size: number) => {
                setPageNumber(1);
                setPageSize(size);
                setSelectedRow([]);
                setRowSelection({});
                fetchOfficeCollectionPage(showActive, 1, size, columnFilters, sorting);
              }}
            />
            <Tooltip title="First Page">
              <AntButton
                className="pagination-edge-btn"
                icon={<DoubleLeftOutlined />}
                disabled={pageNumber <= 1 || totalCount === 0}
                onClick={() => {
                  setPageNumber(1);
                  setSelectedRow([]);
                  setRowSelection({});
                  fetchOfficeCollectionPage(showActive, 1, pageSize, columnFilters, sorting);
                }}
              />
            </Tooltip>
            <Pagination
              className="pagination-number-control"
              current={pageNumber}
              pageSize={pageSize}
              total={totalCount}
              showSizeChanger={false}
              showLessItems
              onChange={(page) => {
                setPageNumber(page);
                setSelectedRow([]);
                setRowSelection({});
                fetchOfficeCollectionPage(showActive, page, pageSize, columnFilters, sorting);
              }}
            />
            <Tooltip title="Last Page">
              <AntButton
                className="pagination-edge-btn"
                icon={<DoubleRightOutlined />}
                disabled={pageNumber >= totalPages || totalCount === 0}
                onClick={() => {
                  setPageNumber(totalPages);
                  setSelectedRow([]);
                  setRowSelection({});
                  fetchOfficeCollectionPage(showActive, totalPages, pageSize, columnFilters, sorting);
                }}
              />
            </Tooltip>
          </div>
        </div>
        <AddOCMapping
          visible={visible}
          setVisibleStatus={setVisible}
          onLoad={handleCancel}
          data={modalData}
          setData={setModalData}
          frameCollection={frameCollection}
          confirmLoading={confirmLoading}
          setConfirmLoading={setConfirmLoading}
          selectedRow={selectedRow}
          setSelectedRow={setSelectedRow}
        />
        <CloseOffice
          visibleClose={closeOfficeVisible}
          setVisibleClose={setCloseOfficeVisible}
          userName={accounts?.[0]?.username}
          onLoad={handleCancel}
        />
        <UploadModal
          visible={openUpload}
          onCancel={() => setOpenUpload(false)}
          stateKey={stateKey}
          importFile={importFile}
          confirmLoading={confirmLoading}
          setConfirmLoading={setConfirmLoading}
        />
        <ValidationModal
          visible={errorModal}
          data={importData}
          onCancel={() => setErrorModal(false)}
          importType={ImportType.OfficeCollectionImport}
        />
        <Modal
          title="Excel export Confirmation"
          okText="Ok"
          open={open}
          centered
          confirmLoading={confirmLoading}
          style={{ top: 20 }}
          onOk={() => exportExcelFile()}
          onCancel={() => setOpen(false)}
        >
          <span>Are you sure you want to export this data?</span>
        </Modal>
        <Modal
          title="Delete Confirmation"
          okText="Ok"
          centered
          confirmLoading={confirmLoading}
          style={{ top: 20 }}
          open={visibleDelete}
          onOk={handleDelete}
          onCancel={() => setVisibleDelete(false)}
        >
          <span>
            Are you sure you wish to delete this office collection mapping?
          </span>
        </Modal>
        <Modal
          title="Bulk Edit Confirmation"
          okText="Ok"
          centered
          confirmLoading={confirmLoading}
          style={{ top: 20 }}
          open={bulkEditModal}
          onOk={(e) => setBulkEditModal(false)}
          cancelButtonProps={{ style: { display: "none" } }}
          onCancel={(e) => setBulkEditModal(false)}
        >
          <span>
            Please ensure all selected rows are for the same Assortment and
            Collection Type.
            <br />
            <p>
              <b>Assortments:</b> {sameType?.Assortment}
              <br />
              <b>Collection Types:</b> {sameType?.CollectionType}
            </p>
          </span>
        </Modal>
      </Spin>
    </>
  );
}
export default OfficeCollectionMapping;
